import { getEnv } from '../config';
const SESSION_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expires_at';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';
const PKCE_STATE_KEY = 'pkce_state';
// Refresh 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const APS_AUTH_BASE_URLS = {
    dev: 'https://developer-dev.api.autodesk.com/authentication/v2',
    stg: 'https://developer-stg.api.autodesk.com/authentication/v2',
    prod: 'https://developer.api.autodesk.com/authentication/v2'
};
// ─── PKCE helpers ────────────────────────────────────────────────────────────
function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}
async function computeCodeChallenge(verifier) {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}
function generateOAuthState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}
function base64UrlEncode(buffer) {
    let binary = '';
    for (const byte of buffer) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
// ─── AuthClient implementation ───────────────────────────────────────────────
class AuthClientImpl {
    constructor() {
        this.currentState = { status: 'loading' };
        this.listeners = new Set();
        this.isInitialized = false;
        this.isLoginInProgress = false;
        this.authConfig = null;
        this._viewerMode = false;
        this.pendingReturnTo = null;
        this.refreshTimer = null;
    }
    consumePendingReturnTo() {
        const val = this.pendingReturnTo;
        this.pendingReturnTo = null;
        return val;
    }
    /**
     * Initialize the auth client.
     * When clientId is absent (development), uses mock authentication.
     * Otherwise, performs the PKCE OAuth 2.0 flow against Autodesk APS.
     */
    initialize(config) {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
        if (!config.clientId) {
            const mockUser = {
                userId: 'dev-user-123',
                email: 'developer@autodesk.com',
                name: 'Local Developer',
                role: 'admin',
                actions: ['READ', 'WRITE', 'SCHEDULE', 'VALIDATE', 'ROLLBACK', 'DELETE']
            };
            const mockTokens = {
                accessToken: 'mock-access-token',
                idToken: 'mock-id-token',
                expiresAt: Date.now() + 24 * 60 * 60 * 1000
            };
            // Dev mode: use sessionStorage so mock tokens don't persist across browser restarts
            sessionStorage.setItem(SESSION_TOKEN_KEY, 'mock-access-token');
            this.setState({ status: 'authenticated', user: mockUser, tokens: mockTokens });
            return;
        }
        this.authConfig = config;
        const params = new URLSearchParams(window.location.search);
        if (params.has('code') || params.has('error')) {
            // Capture the full callback URL NOW (synchronously) before React renders.
            // React Router's <Navigate> fires during the first render and strips ?code=
            // from window.location. We must save the URL before that happens.
            const callbackUrl = window.location.href;
            // Clean the URL immediately so React Router sees "/" and doesn't try to
            // re-navigate while the async token exchange is in progress.
            window.history.replaceState({}, '', '/');
            void this.handleOAuthCallback(callbackUrl);
        }
        else {
            void this.restoreSession();
        }
    }
    async handleOAuthCallback(callbackUrl) {
        const returnTo = sessionStorage.getItem('auth_return_to') || '/';
        sessionStorage.removeItem('auth_return_to');
        try {
            // Set BEFORE handleCallback so React can read it during the re-render triggered by setState
            if (returnTo !== '/')
                this.pendingReturnTo = returnTo;
            await this.handleCallback(callbackUrl);
            window.history.replaceState({}, '', returnTo);
        }
        catch (err) {
            this.pendingReturnTo = null;
            const message = err instanceof Error ? err.message : String(err);
            console.error('[AuthClient] OAuth callback failed:', err);
            this.setState({ status: 'error', error: `Authentication failed: ${message}` });
            window.history.replaceState({}, '', '/');
        }
    }
    async restoreSession() {
        let token = sessionStorage.getItem(SESSION_TOKEN_KEY);
        if (!token) {
            this.setState({ status: 'unauthenticated' });
            return;
        }
        const expiresAt = Number(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
        const storedRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        // Proactively refresh if the token is expired or expiring within the buffer window
        if (expiresAt > 0 && Date.now() > expiresAt - REFRESH_BUFFER_MS) {
            if (!storedRefreshToken) {
                this.clearStoredTokens();
                this.setState({ status: 'unauthenticated' });
                return;
            }
            const refreshed = await this.tryRefreshSession(storedRefreshToken);
            if (!refreshed) {
                this.clearStoredTokens();
                this.setState({ status: 'unauthenticated' });
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            token = sessionStorage.getItem(SESSION_TOKEN_KEY);
        }
        const user = await this.fetchUserProfile(token);
        if (user === 'denied') {
            this.clearStoredTokens();
            this.setState({ status: 'denied' });
            return;
        }
        if (user) {
            const newExpiresAt = Number(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
            this.setState({
                status: 'authenticated',
                user,
                tokens: {
                    accessToken: token,
                    idToken: '',
                    expiresAt: newExpiresAt,
                    refreshToken: sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined
                }
            });
            this.scheduleTokenRefresh(newExpiresAt);
        }
        else {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
        }
    }
    /**
     * Attempt a silent token refresh using the stored refresh token.
     * Stores new tokens in sessionStorage on success.
     * Returns true if refresh succeeded, false otherwise.
     */
    async tryRefreshSession(refreshToken) {
        try {
            const backendUrl = getEnv('API_BASE_URL') || 'http://localhost:3000/dev/v1';
            const response = await fetch(`${backendUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
            });
            if (!response.ok)
                return false;
            const { data } = (await response.json());
            if (!data.accessToken || !data.expiresAt)
                return false;
            sessionStorage.setItem(SESSION_TOKEN_KEY, data.accessToken);
            if (data.refreshToken)
                sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
            if (data.expiresAt)
                sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(data.expiresAt));
            return true;
        }
        catch (err) {
            console.warn('[AuthClient] Silent token refresh failed:', err);
            return false;
        }
    }
    /**
     * Schedule a silent token refresh before the current token expires.
     * Clears any previously scheduled refresh timer.
     */
    scheduleTokenRefresh(expiresAt) {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (expiresAt <= 0)
            return;
        const delay = expiresAt - Date.now() - REFRESH_BUFFER_MS;
        if (delay <= 0)
            return; // already expired or within buffer; restoreSession handles it
        this.refreshTimer = setTimeout(() => {
            void this.silentRefresh();
        }, delay);
    }
    /**
     * Silently refresh the access token in the background.
     * Called automatically by the scheduled refresh timer.
     */
    async silentRefresh() {
        const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
            return;
        }
        const refreshed = await this.tryRefreshSession(refreshToken);
        if (!refreshed) {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const newToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
        const newExpiresAt = Number(sessionStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
        if (this.currentState.status === 'authenticated') {
            this.setState({
                status: 'authenticated',
                user: this.currentState.user,
                tokens: {
                    accessToken: newToken,
                    idToken: '',
                    expiresAt: newExpiresAt,
                    refreshToken: sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined
                }
            });
        }
        this.scheduleTokenRefresh(newExpiresAt);
    }
    /** Remove all persisted token data from sessionStorage. */
    clearStoredTokens() {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
    /**
     * Trigger Autodesk OAuth redirect (production) or force mock auth (dev).
     * Guards against concurrent calls (e.g. React StrictMode double-invoke).
     */
    login() {
        if (!this.authConfig) {
            // Dev mode (no clientId configured)
            if (this.currentState.status !== 'authenticated') {
                const mockUser = {
                    userId: 'dev-user-123',
                    email: 'developer@autodesk.com',
                    name: 'Local Developer',
                    role: 'admin',
                    actions: ['READ', 'WRITE', 'SCHEDULE', 'VALIDATE', 'ROLLBACK', 'DELETE']
                };
                const mockTokens = {
                    accessToken: 'mock-access-token',
                    idToken: 'mock-id-token',
                    expiresAt: Date.now() + 24 * 60 * 60 * 1000
                };
                sessionStorage.setItem(SESSION_TOKEN_KEY, 'mock-access-token');
                this.setState({ status: 'authenticated', user: mockUser, tokens: mockTokens });
            }
            return;
        }
        if (this.isLoginInProgress)
            return;
        this.isLoginInProgress = true;
        void this.startPkceLogin().catch((err) => {
            this.isLoginInProgress = false;
            const message = err instanceof Error ? err.message : String(err);
            this.setState({ status: 'error', error: `Login failed: ${message}` });
        });
    }
    /**
     * Build PKCE authorization URL and redirect the browser to APS.
     * The code_verifier is stored in sessionStorage so it survives the page
     * navigation and can be retrieved in handleCallback after APS redirects back.
     */
    async startPkceLogin() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { clientId, redirectUri, scope, env } = this.authConfig;
        const verifier = generateCodeVerifier();
        const challenge = await computeCodeChallenge(verifier);
        const state = generateOAuthState();
        // sessionStorage persists through same-tab page navigations (APS redirect)
        // but is cleared when the tab is closed — appropriate scope for an auth code.
        sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
        sessionStorage.setItem(PKCE_STATE_KEY, state);
        const baseUrl = APS_AUTH_BASE_URLS[env] ?? APS_AUTH_BASE_URLS.stg;
        const authParams = new URLSearchParams({
            response_type: 'code',
            client_id: clientId,
            redirect_uri: redirectUri,
            scope,
            state,
            code_challenge: challenge,
            code_challenge_method: 'S256'
        });
        window.location.assign(`${baseUrl}/authorize?${authParams.toString()}`);
    }
    /**
     * Handle OAuth callback. Reads the PKCE verifier from sessionStorage and
     * exchanges the authorization code via the backend BFF (keeps client_secret
     * server-side). Stores the access token, refresh token, and expiry in
     * sessionStorage so the session persists across browser restarts.
     */
    async handleCallback(callbackUrl) {
        const url = new URL(callbackUrl ?? window.location.href);
        const code = url.searchParams.get('code');
        if (!code)
            throw new Error('No authorization code in callback URL');
        const returnedState = url.searchParams.get('state');
        const savedState = sessionStorage.getItem(PKCE_STATE_KEY);
        sessionStorage.removeItem(PKCE_STATE_KEY);
        if (!returnedState || returnedState !== savedState) {
            throw new Error('OAuth state mismatch — possible CSRF attack');
        }
        const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
        if (!codeVerifier)
            throw new Error('No PKCE code verifier found in sessionStorage');
        const redirectUri = this.authConfig?.redirectUri ?? window.location.origin + '/';
        const backendUrl = getEnv('API_BASE_URL') || 'http://localhost:3000/dev/v1';
        const response = await fetch(`${backendUrl}/auth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, codeVerifier, redirectUri })
        });
        if (!response.ok) {
            const errBody = (await response.json().catch(() => ({})));
            throw new Error(`Backend token exchange failed (${response.status}): ${String(errBody.error ?? '')}`);
        }
        const { data } = (await response.json());
        if (!data.accessToken)
            throw new Error('No access token returned from backend');
        sessionStorage.setItem(SESSION_TOKEN_KEY, data.accessToken);
        if (data.refreshToken)
            sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        if (data.expiresAt)
            sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(data.expiresAt));
        const user = await this.fetchUserProfile(data.accessToken);
        if (user === 'denied') {
            this.clearStoredTokens();
            this.setState({ status: 'denied' });
            return;
        }
        if (!user)
            throw new Error('Failed to fetch user profile from backend');
        const expiresAt = data.expiresAt ?? 0;
        this.setState({
            status: 'authenticated',
            user,
            tokens: {
                accessToken: data.accessToken,
                idToken: '',
                expiresAt,
                refreshToken: data.refreshToken
            }
        });
        this.scheduleTokenRefresh(expiresAt);
    }
    /**
     * Authenticate with username and password credentials via the backend.
     * Stores tokens and fetches the user profile on success.
     */
    async loginWithCredentials(username, password) {
        const backendUrl = getEnv('API_BASE_URL') || 'http://localhost:3000/dev/v1';
        const response = await fetch(`${backendUrl}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });
        if (!response.ok) {
            const errBody = (await response.json().catch(() => ({})));
            throw new Error(`Login failed (${response.status}): ${String(errBody.error ?? 'Invalid credentials')}`);
        }
        const { data } = (await response.json());
        if (!data.accessToken)
            throw new Error('No access token returned from backend');
        sessionStorage.setItem(SESSION_TOKEN_KEY, data.accessToken);
        if (data.refreshToken)
            sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        if (data.expiresAt)
            sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(data.expiresAt));
        const user = await this.fetchUserProfile(data.accessToken);
        if (user === 'denied') {
            this.clearStoredTokens();
            this.setState({ status: 'denied' });
            return;
        }
        if (!user)
            throw new Error('Failed to fetch user profile from backend');
        const expiresAt = data.expiresAt ?? 0;
        this.setState({
            status: 'authenticated',
            user,
            tokens: {
                accessToken: data.accessToken,
                idToken: '',
                expiresAt,
                refreshToken: data.refreshToken
            }
        });
        this.scheduleTokenRefresh(expiresAt);
    }
    /**
     * Clear local session and redirect to Autodesk APS logout endpoint.
     */
    logout() {
        this.clearStoredTokens();
        sessionStorage.removeItem(SESSION_TOKEN_KEY); // clear dev-mode mock token too
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (this.authConfig) {
            // A full-page redirect to APS is imminent — skip the local state
            // update. Setting 'unauthenticated' here would let ProtectedRoute's
            // auto-login effect race this navigation with its own /authorize
            // redirect, which (since the APS session cookie is still live) can
            // silently re-authenticate the user before this logout redirect takes
            // effect, making sign-out appear to do nothing. The page reloads fresh
            // after the logout round-trip, and initializeAuth() re-derives
            // 'unauthenticated' from the now-cleared tokens on its own.
            const { env } = this.authConfig;
            const baseUrl = APS_AUTH_BASE_URLS[env] ?? APS_AUTH_BASE_URLS.stg;
            const postLogoutUri = encodeURIComponent(window.location.origin);
            window.location.assign(`${baseUrl}/logout?post_logout_redirect_uri=${postLogoutUri}`);
            return;
        }
        this.setState({ status: 'unauthenticated' });
    }
    /**
     * Get current access token from storage (sync read for axios interceptor).
     * Production tokens are in sessionStorage; dev mock token is in sessionStorage.
     */
    getAccessToken() {
        if (this.currentState.status !== 'authenticated')
            return null;
        return sessionStorage.getItem(SESSION_TOKEN_KEY) ?? sessionStorage.getItem(SESSION_TOKEN_KEY);
    }
    /**
     * Get current user profile.
     */
    getUser() {
        if (this.currentState.status !== 'authenticated')
            return null;
        return this.currentState.user;
    }
    /**
     * Get current auth state.
     */
    getState() {
        return this.currentState;
    }
    /**
     * Subscribe to state changes. Returns an unsubscribe function.
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    /**
     * Fetch the authenticated user's profile from the backend.
     * The backend is the single source of truth for role and permissions.
     * @returns `'denied'` if the backend rejected the caller as non-Autodesk
     *   (403 `AUTH_FORBIDDEN`); `null` on any other failure.
     */
    async fetchUserProfile(token) {
        try {
            const backendUrl = getEnv('API_BASE_URL') || 'http://localhost:3000/dev/v1';
            const response = await fetch(`${backendUrl}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 403)
                return 'denied';
            if (!response.ok)
                return null;
            const { data } = (await response.json());
            return data ?? null;
        }
        catch {
            return null;
        }
    }
    /**
     * Toggle viewer mode for admin users.
     * When enabled, the UI treats the current user as a viewer (READ-only).
     * Has no effect if the underlying role is not admin.
     */
    setViewerMode(enabled) {
        if (this.currentState.status !== 'authenticated' || this.currentState.user.role !== 'admin') {
            return;
        }
        this._viewerMode = enabled;
        this.setState(this.currentState);
    }
    setState(state) {
        if (state.status === 'authenticated') {
            this.currentState = { ...state, viewerMode: this._viewerMode };
        }
        else {
            this._viewerMode = false;
            this.currentState = state;
        }
        this.listeners.forEach((listener) => listener(this.currentState));
    }
}
// Singleton instance
export const AuthClient = new AuthClientImpl();
/**
 * Initialize auth client with configuration from environment variables.
 * Call once at app startup.
 *
 * IMPORTANT: REACT_APP_AUTH_CLIENT_ID must match the backend's FORGE_CLIENT_ID.
 * Both use the same APS confidential client — the frontend omits the secret,
 * the backend BFF adds it for the token exchange.
 */
export function initializeAuth() {
    const apsEnv = getEnv('APS_ENVIRONMENT');
    const env = apsEnv === 'prod' ? 'prod' : apsEnv === 'dev' ? 'dev' : 'stg';
    const config = {
        clientId: getEnv('AUTH_CLIENT_ID'),
        redirectUri: window.location.origin + '/',
        scope: 'user-profile:read',
        env
    };
    AuthClient.initialize(config);
}
